import { message } from 'antd';
import { useHrPayrollStore } from '../stores/hr-payroll-store';
import { customInstance } from '@/shared/api/mutator/custom-instance';
import { PayrollPeriod, PayrollEntry, SalaryStructure } from '../types';

export function usePayroll() {
  const store = useHrPayrollStore();

  const fetchPeriods = async () => {
    store.setLoading(true);
    try {
      const data = await customInstance<any>({ url: '/api/hr/payroll?size=50', method: 'GET' });
      store.setPeriods(data.content ?? data);
      return data.content ?? data;
    } catch {
      message.error('Failed to load payroll periods');
    } finally {
      store.setLoading(false);
    }
  };

  const fetchPeriod = async (id: number) => {
    try {
      const data = await customInstance<PayrollPeriod>({ url: `/api/hr/payroll/${id}`, method: 'GET' });
      store.setActivePeriod(data);
      return data;
    } catch {
      message.error('Failed to load payroll period');
    }
  };

  const createPeriod = async (dto: Partial<PayrollPeriod>) => {
    try {
      const data = await customInstance<PayrollPeriod>({ url: '/api/hr/payroll', method: 'POST', data: dto });
      message.success('Payroll period created');
      await fetchPeriods();
      return data;
    } catch {
      message.error('Failed to create payroll period');
    }
  };

  const generateEntries = async (periodId: number) => {
    store.setLoading(true);
    try {
      await customInstance({ url: `/api/hr/payroll/${periodId}/generate`, method: 'POST' });
      message.success('Payroll entries generated');
      await fetchPeriod(periodId);
    } catch {
      message.error('Failed to generate payroll entries');
    } finally {
      store.setLoading(false);
    }
  };

  const fetchEntries = async (periodId: number) => {
    store.setLoading(true);
    try {
      const data = await customInstance<PayrollEntry[]>({ url: `/api/hr/payroll/${periodId}/entries`, method: 'GET' });
      store.setEntries(Array.isArray(data) ? data : []);
      return data;
    } catch {
      message.error('Failed to load payroll entries');
    } finally {
      store.setLoading(false);
    }
  };

  const processPayroll = async (periodId: number) => {
    store.setLoading(true);
    try {
      await customInstance({ url: `/api/hr/payroll/${periodId}/process`, method: 'POST' });
      message.success('Payroll processed — pending approval');
      await fetchPeriod(periodId);
    } catch {
      message.error('Failed to process payroll');
    } finally {
      store.setLoading(false);
    }
  };

  const approvePayroll = async (periodId: number) => {
    store.setLoading(true);
    try {
      await customInstance({ url: `/api/hr/payroll/${periodId}/approve`, method: 'POST' });
      message.success('Payroll approved and GL entries posted');
      await fetchPeriod(periodId);
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Failed to approve payroll');
    } finally {
      store.setLoading(false);
    }
  };

  const markAsPaid = async (periodId: number) => {
    store.setLoading(true);
    try {
      await customInstance({ url: `/api/hr/payroll/${periodId}/mark-paid`, method: 'POST' });
      message.success('Payroll marked as paid');
      await fetchPeriod(periodId);
    } catch {
      message.error('Failed to mark payroll as paid');
    } finally {
      store.setLoading(false);
    }
  };

  const cancelPayroll = async (periodId: number) => {
    try {
      await customInstance({ url: `/api/hr/payroll/${periodId}/cancel`, method: 'POST' });
      message.success('Payroll cancelled');
      await fetchPeriods();
    } catch {
      message.error('Failed to cancel payroll');
    }
  };

  const fetchSalaryStructures = async () => {
    try {
      return await customInstance<SalaryStructure[]>({ url: '/api/hr/salary-structures', method: 'GET' });
    } catch {
      message.error('Failed to load salary structures');
      return [];
    }
  };

  const saveSalaryStructure = async (dto: Partial<SalaryStructure>) => {
    try {
      const method = dto.id ? 'PUT' : 'POST';
      const url = dto.id ? `/api/hr/salary-structures/${dto.id}` : '/api/hr/salary-structures';
      const data = await customInstance<SalaryStructure>({ url, method, data: dto });
      message.success('Salary structure saved');
      return data;
    } catch {
      message.error('Failed to save salary structure');
    }
  };

  return {
    ...store,
    fetchPeriods,
    fetchPeriod,
    createPeriod,
    generateEntries,
    fetchEntries,
    processPayroll,
    approvePayroll,
    markAsPaid,
    cancelPayroll,
    fetchSalaryStructures,
    saveSalaryStructure,
  };
}
